import React, { ReactNode, useCallback, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { faSort } from '@fortawesome/pro-duotone-svg-icons';
import { faSort as faSortSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { ColumnElement } from 'framework/Grid/columns/types';

import { EMPTY_FUNCTION, GRID_MIN_CELL_WIDTH } from '../../../constants';
import { useGridContext } from '../context/GridContext';
import { SORT_DIRECTION } from '../enums';

import { GridHeaderElement } from './elements';
import { GridHeaderDrag, GridHeaderProps } from './types';

import cn from '../Grid.module.scss';

export const GridHeader = ({
  onMouseDown = EMPTY_FUNCTION,
  column,
  index,
  style,
  children,
}: GridHeaderProps) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const [is_resizing, setResizing] = useState(false);
  const { moveColumn, updateColumnSortDirection, resizeColumn, theme } = useGridContext();

  const { fixed, sort_direction, filter_field, sort_field, text_align } = column.props;
  const is_fixed = fixed === 'left' || fixed === 'right';

  const [{ is_dragging }, drag] = useDrag<GridHeaderDrag, unknown, { is_dragging: boolean }>({
    type: 'COLUMN',
    item: { index, type: 'COLUMN' },
    canDrag: !is_fixed,
    collect: (monitor) => ({ is_dragging: monitor.isDragging() }),
  });

  const [, drop] = useDrop<GridHeaderDrag, unknown, unknown>({
    accept: 'COLUMN',
    drop(item) {
      if (item.index !== index) {
        moveColumn(item.index, index);
      }
    },
  });

  const combineRefs = useCallback(
    (...refs: Array<React.Ref<HTMLDivElement> | null>) =>
      (node: HTMLDivElement | null) => {
        refs.forEach((ref) => {
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
          }
        });
      },
    []
  );

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setResizing(true);

      const startX = e.clientX;
      const startWidth = headerRef.current?.offsetWidth || 0;

      const handleMouseMove = (e: MouseEvent) => {
        const diff = e.clientX - startX;
        const newWidth = Math.max(startWidth + diff, GRID_MIN_CELL_WIDTH);
        resizeColumn(index, newWidth);
      };

      const handleMouseUp = () => {
        setTimeout(() => setResizing(false), 100);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [index, resizeColumn]
  );

  const handleSortDirection = useCallback(() => {
    if (is_resizing || filter_field || !sort_field) return;
    updateColumnSortDirection(index, (sort_direction as SORT_DIRECTION) || null);
  }, [is_resizing, filter_field, sort_field, updateColumnSortDirection, index, sort_direction]);

  return (
    <GridHeaderElement
      ref={combineRefs(headerRef, is_fixed ? null : drag, drop)}
      onClick={handleSortDirection}
      onMouseDown={(e) => {
        if (headerRef?.current?.contains(e.target as Node)) {
          onMouseDown();
        }
      }}
      className={classNames('column', cn.header, {
        [cn.dragging]: is_dragging,
        [cn.sorted]: !!sort_direction,
      })}
      theme={theme}
      is_resizing={is_resizing}
      sort_field={sort_field}
      style={{ ...style, justifyContent: text_align }}
    >
      {children}
      <div
        className={cn.handle}
        onMouseDown={handleResizeStart}
        onClick={(e) => e.stopPropagation()}
      />
    </GridHeaderElement>
  );
};

GridHeader.Sort = ({ column, children }: { column: ColumnElement; children: ReactNode }) => {
  const { sort_direction, sort_field } = column.props;
  return (
    <div className={cn.sort}>
      {children}
      {sort_field && sort_direction === null && (
        <FontAwesomeIcon className={cn.icon} icon={faSortSolid} />
      )}
      {sort_field && sort_direction === 'asc' && (
        <FontAwesomeIcon className={cn.icon} icon={faSort} />
      )}
      {sort_field && sort_direction === 'desc' && (
        <FontAwesomeIcon className={classNames(cn.icon, cn.rotate)} icon={faSort} />
      )}
    </div>
  );
};
